import { ApolloLink } from 'apollo-link'
import get from 'lodash/get'
import { translateError } from 'src/services/apollo'
import { debounceUntilStable } from 'labguard-cdk'

/**
 * Custom link enabling notifications on graphql operations
 */
export default new ApolloLink((operation, forward) => {
  // get intl and notify api
  const { intl, notify } = operation.getContext().cache

  if (!intl || !notify) {
    // directly forward operation if intl or notify is not yet available
    return forward(operation)
  }

  const { notifyLoading, notifyError, notifySuccess, closeNotification } = notify

  // get notifications config
  const config = getNotifyConfig(operation)

  // display "loading" notification if enabled
  const notificationId = config.loading ? notifyLoading(config.loading) : undefined

  return forward(operation).map(value => {
    if (value.errors && config.error) {
      // display "error" notification if enabled and errors available
      closeNotification(notificationId)
      value.errors
        .map(error => translateError(error, intl))
        .filter(Boolean)
        .forEach(message => debounceUntilStable({ message, ...config.error }, 200, notifyError))
    } else if (config.success) {
      // display "success" notification if enabled
      notifySuccess(config.success, notificationId)
    } else if (notificationId) {
      // hide "loading" notification otherwise
      closeNotification(notificationId)
    }

    // return untouched value
    return value
  })
})

/**
 * Default notifications settings for queries
 */
const queriesDefaultNotifyConfig = {
  loading: false,
  error: true,
  success: false,
}

/**
 * Default notifications settings for mutations
 */
const mutationsDefaultNotifyConfig = {
  loading: true,
  error: true,
  success: true,
}

/**
 * Check if a graphql operation is a mutation
 * @param {Object} operation
 * @return {Boolean}
 */
const isMutation = operation =>
  get(operation, 'query.definitions', []).some(definition => definition.operation === 'mutation')

/**
 * Get the configuration for notifying the current operation
 * @param {Object} operation
 * @return {Object}
 */
const getNotifyConfig = operation => ({
  ...(isMutation(operation) ? mutationsDefaultNotifyConfig : queriesDefaultNotifyConfig),
  ...operation.getContext().notify,
})
